import {
  isActionCardWaiting,
  teammateMessageActionCardKind,
} from '@littlebird/action-cards';
import { cardState } from '@littlebird/action-cards-view';
import { useStores, type ActionCard } from '@littlebird/sdk';

import { withdrawnTextOf } from '@/features/action-cards-view/feed/historyRowView';
import { useWikiPeopleOfUsers } from '@/features/people-wiki/use-wiki-person-of-user';
import { NEW_CHAT_ID } from '@/utils/constants';

import { hivemindAnswerAfterAssistantMessage } from './hivemind-conversations-messages';
import {
  isAnswerUnseen,
  messageGroupOf,
} from './hivemind-conversations-status';
import { type HivemindAnswerRow } from './hivemind-conversations-table';
import { useHivemindConversations } from './use-hivemind-conversations';

export type HivemindGroup = {
  readonly chatId: string;
  // The assistant message that drafted the group's cards, which tells it from another message with the same text
  readonly messageId: string;
  readonly cards: readonly ActionCard[];
  // Only the first card draws the group, and the others draw nothing
  readonly isFirst: boolean;
  readonly isDraft: boolean;
  // The cards whose answer the user has not marked as seen, once nobody is left to answer
  readonly unseenAnswers: readonly ActionCard[];
  readonly text: string;
  // What the person said, when the group is a reply to it
  readonly incomingText: string | null;
  // Whether the message went out without the user approving it
  readonly sentAutomatically: boolean;
  readonly title: string;
  readonly rows: readonly HivemindAnswerRow[];
};

const nameOf = (card: ActionCard) =>
  teammateMessageActionCardKind.decode(card.content)?.teammate.name ?? '';

// The people in the order the message was drafted for them, whatever has happened to their cards since
const draftedOrder = (a: ActionCard, b: ActionCard) =>
  a.createdAt.getTime() - b.createdAt.getTime() ||
  nameOf(a).localeCompare(nameOf(b));

/** The card's message to one person or several, with the title it shows and a row for each person. Null for any other card. */
export function useHivemindGroup(card: ActionCard): HivemindGroup | null {
  'use no memo';
  const { actionCards, messages } = useStores();
  const wikiPersonOf = useWikiPeopleOfUsers();
  const { chatId } = card;
  const chatMessages = chatId ? messages.getMessagesByThreadId(chatId) : [];
  const cards = chatId
    ? messageGroupOf(card, actionCards.getByChatId(chatId))
    : [];
  const first = cards[0];
  // Each card asks, and only the first uses the answer
  const { threads } = useHivemindConversations(
    first?.id === card.id && chatId ? chatId : NEW_CHAT_ID
  );
  if (!chatId || !first) return null;
  const drafts = cards.filter(({ status }) => status === 'draft');
  // Nothing has gone out until a card runs or completes, so a person who left meanwhile does not end the editing
  const isDraft =
    drafts.length > 0 &&
    !cards.some(({ status }) => status === 'running' || status === 'completed');
  const rows = [...cards]
    .sort(draftedOrder)
    .flatMap((member): HivemindAnswerRow[] => {
      const decoded = teammateMessageActionCardKind.decode(member.content);
      const teammate = decoded?.teammate;
      if (!teammate) return [];
      const conversation = threads.find(
        ({ teammate: { id } }) => id === teammate.id
      );
      const messageAnswer =
        member.status === 'completed' && !decoded.incoming_text
          ? hivemindAnswerAfterAssistantMessage(
              chatMessages,
              first.assistantMessageId,
              teammate.id
            )
          : null;
      const answered =
        messageAnswer === null
          ? conversation?.status === 'replied'
          : messageAnswer.answer !== null;
      return [
        {
          id: member.id,
          name: wikiPersonOf(teammate.id)?.name ?? teammate.name,
          personId: teammate.id,
          status:
            member.status === 'revoked'
              ? withdrawnTextOf(cardState(member.row))
              : member.status === 'draft'
                ? 'Draft'
                : member.status === 'running'
                  ? 'Sending…'
                  : decoded.incoming_text
                    ? decoded.sent_automatically
                      ? 'Sent automatically'
                      : 'Sent'
                    : answered
                      ? 'Answered'
                      : conversation?.status === 'disconnected'
                        ? 'No longer connected'
                        : conversation?.status === 'no_reply'
                          ? "Didn't reply"
                          : 'Waiting',
          answer:
            messageAnswer === null
              ? (conversation?.answer ?? null)
              : messageAnswer.answer,
        },
      ];
    });
  const answered = rows.filter(({ status }) => status === 'Answered').length;
  const content = teammateMessageActionCardKind.decode(first.content);
  const [only] = rows.length === 1 ? rows : [];
  const incomingText = content?.incoming_text || null;
  return {
    chatId,
    messageId: first.assistantMessageId ?? first.id,
    cards,
    isFirst: first.id === card.id,
    isDraft,
    unseenAnswers: cards.some(isActionCardWaiting)
      ? []
      : cards.filter(isAnswerUnseen),
    text: content?.sent_text ?? content?.draft ?? '',
    incomingText,
    sentAutomatically: content?.sent_automatically === true,
    title: only
      ? incomingText
        ? only.status.startsWith('Sent')
          ? `Replied to ${only.name}`
          : `Reply to ${only.name}`
        : only.status === 'Answered'
          ? `${only.name} answered`
          : only.status === 'Waiting'
            ? `Waiting for ${only.name}`
            : `Message to ${only.name}`
      : isDraft
        ? `Message to ${drafts.length} ${drafts.length === 1 ? 'person' : 'people'}`
        : answered === cards.length
          ? `All ${cards.length} answered`
          : `${answered} of ${cards.length} answered`,
    rows,
  };
}
